import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import './AportesProgramados.css';

/**
 * AportesProgramados.js — cobranças de aporte RECORRENTES na conta Stark Bank.
 * ---------------------------------------------------------------------------
 * Irmã da ReceberAportes: aqui o usuário programa um aporte (dia do mês + hora +
 * valor + pagador + método) e o servidor gera PIX/boleto sozinho, guarda o PDF
 * no Drive, registra no histórico e envia por e-mail (até 5 caixas).
 *
 * Mesma disciplina de ambiente da tela irmã:
 *  - Nada renderiza antes do GET /account confirmar sandbox/produção.
 *  - Selo PRODUÇÃO/SANDBOX no cabeçalho; alerta forte em sandbox.
 *  - O escopo capturado na abertura do modal é reconferido no submit: se o
 *    ambiente virou, o cadastro é abortado (um agendamento nasce preso ao
 *    ambiente e não pode migrar por acidente).
 */

const MAX_EMAILS = 5;
const VALOR_MAX = 1_000_000;

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ENV_LABEL = { production: 'PRODUÇÃO', sandbox: 'SANDBOX' };
const envLabelOf = (e) => ENV_LABEL[e] || String(e || '').toUpperCase() || '—';

const METODO_LABEL = { pix: 'PIX', boleto: 'Boleto', ambos: 'PIX + Boleto' };
const STATUS_LABEL = { gerado: 'Gerado', parcial: 'Parcial', erro: 'Erro' };
const EMAIL_LABEL = { sent: 'Enviado', failed: 'Falhou', skipped: 'Sem envio' };

const onlyDigits = (v) => String(v ?? '').replace(/\D/g, '');
const asArray = (v) => (Array.isArray(v) ? v : []);

const fmtBRL = (v) => (parseFloat(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Formata datas que vêm do servidor já em horário-parede de SP, sem re-ajustar
 *  fuso: extrai Y/M/D/H/min crus da string (space ou ISO) e mostra como está. */
const fmtSP = (value, comHora = true) => {
    if (!value) return '—';
    const m = String(value).match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!m) return String(value);
    const [, y, mo, d, h, mi] = m;
    return comHora ? `${d}/${mo}/${y} ${h}:${mi}` : `${d}/${mo}/${y}`;
};

const apiErr = (e, fallback = 'Falha na comunicação com o servidor.') => {
    if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') return null;
    const status = e?.response?.status;
    if (status === 403) return 'Seu perfil não tem permissão para esta ação. Fale com um administrador.';
    if (status === 409) return e?.response?.data?.error || 'A conta Stark Bank não está conectada.';
    return e?.response?.data?.error || e?.message || fallback;
};

const parseValor = (raw, { min = 0.01, max = VALOR_MAX } = {}) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const s = String(raw).trim().replace(/\s/g, '');
    const norm = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
    const n = Number(norm);
    if (!Number.isFinite(n) || n < min || n > max) return null;
    return Math.round(n * 100) / 100;
};

const taxIdValido = (raw) => {
    const d = onlyDigits(raw);
    return d.length === 11 || d.length === 14;
};

// Escopo de ambiente compartilhado (mesma ideia da ReceberAportes).
const SCOPE = { current: null };
const scopeOf = (account) => (account ? `${account.environment || '?'}::${account.stark_project_id || '?'}` : null);
const escopoAindaVale = (aoAbrir) => (!aoAbrir || !SCOPE.current ? true : SCOPE.current === aoAbrir);

// ── Hooks de UI ─────────────────────────────────────────────────────────────
let SCROLL_LOCKS = 0;
function useBodyScrollLock(active) {
    useEffect(() => {
        if (!active) return undefined;
        SCROLL_LOCKS += 1;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            SCROLL_LOCKS = Math.max(0, SCROLL_LOCKS - 1);
            if (SCROLL_LOCKS === 0) document.body.style.overflow = prev || '';
        };
    }, [active]);
}

function useEscapeKey(handler, active = true) {
    const ref = useRef(handler);
    ref.current = handler;
    useEffect(() => {
        if (!active) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); ref.current?.(); } };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [active]);
}

function EnvBadge({ environment, className = '' }) {
    if (!environment) return null;
    const prod = environment === 'production';
    return <span className={`ap-env-badge ${prod ? 'is-prod' : 'is-sbx'} ${className}`}>● {envLabelOf(environment)}</span>;
}

function Pill({ children, tone = 'neutral' }) {
    return <span className={`ap-pill ap-pill--${tone}`}>{children}</span>;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export default function AportesProgramados() {
    const [hydrated, setHydrated] = useState(false);
    const [connected, setConnected] = useState(false);
    const [account, setAccount] = useState(null);
    const [tab, setTab] = useState('agendamentos');

    const [agendamentos, setAgendamentos] = useState([]);
    const [loadingAg, setLoadingAg] = useState(false);
    const [editing, setEditing] = useState(null); // objeto p/ editar, {} p/ novo, null fechado

    const aliveRef = useRef(true);
    const inFlightRef = useRef(null);
    useEffect(() => () => { aliveRef.current = false; inFlightRef.current?.abort(); }, []);

    const environment = account?.environment || null;
    const isProd = environment === 'production';
    const scope = scopeOf(account);

    const carregarAgendamentos = useCallback(async (signal) => {
        setLoadingAg(true);
        try {
            const { data } = await api.get('/api/baas/account/aportes', { signal });
            if (!aliveRef.current) return;
            setAgendamentos(asArray(data?.agendamentos));
        } catch (e) {
            const msg = apiErr(e);
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (aliveRef.current) setLoadingAg(false);
        }
    }, []);

    const load = useCallback(async () => {
        inFlightRef.current?.abort();
        const controller = new AbortController();
        inFlightRef.current = controller;
        try {
            const { data } = await api.get('/api/baas/account', { signal: controller.signal });
            if (!aliveRef.current) return;

            const nextAccount = data?.account || null;
            const isConnected = !!data?.connected;
            const nextScope = scopeOf(nextAccount);

            if (SCOPE.current && SCOPE.current !== nextScope) {
                setAgendamentos([]);
                setEditing(null);
            }
            SCOPE.current = nextScope;

            setAccount(nextAccount);
            setConnected(isConnected);

            if (isConnected) carregarAgendamentos(controller.signal);
            else setAgendamentos([]);
        } catch (e) {
            const msg = apiErr(e);
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (inFlightRef.current === controller) inFlightRef.current = null;
            if (aliveRef.current && !controller.signal.aborted) setHydrated(true);
        }
    }, [carregarAgendamentos]);

    useEffect(() => { load(); }, [load]);
    useBodyScrollLock(!!editing);

    const totalMensal = useMemo(
        () => agendamentos.filter((a) => a.ativo).reduce((s, a) => s + (Number(a.amount) || 0), 0),
        [agendamentos],
    );

    const toggleAtivo = async (ag) => {
        try {
            const { data } = await api.patch(`/api/baas/account/aportes/${ag.id}/toggle`);
            setAgendamentos((prev) => prev.map((x) => (x.id === ag.id
                ? { ...x, ativo: data.ativo, proximo_disparo: data.proximo_disparo ?? x.proximo_disparo }
                : x)));
            toast.info(data.ativo ? 'Aporte reativado.' : 'Aporte pausado.');
        } catch (e) {
            const msg = apiErr(e);
            if (msg) toast.error(msg);
        }
    };

    const excluir = async (ag) => {
        if (!window.confirm(`Excluir o aporte programado "${ag.titulo}"? O histórico é mantido.`)) return;
        try {
            await api.delete(`/api/baas/account/aportes/${ag.id}`);
            setAgendamentos((prev) => prev.filter((x) => x.id !== ag.id));
            toast.success('Aporte programado excluído.');
        } catch (e) {
            const msg = apiErr(e);
            if (msg) toast.error(msg);
        }
    };

    const gerarAgora = async (ag) => {
        if (!window.confirm(`Gerar agora o aporte "${ag.titulo}" de ${fmtBRL(ag.amount)} e enviar por e-mail?`)) return;
        const tId = toast.loading('Gerando cobranças e enviando...');
        try {
            const { data } = await api.post(`/api/baas/account/aportes/${ag.id}/gerar-agora`);
            toast.update(tId, {
                render: data.message || 'Aporte gerado.',
                type: data.status === 'parcial' ? 'warning' : 'success',
                isLoading: false, autoClose: 4000,
            });
        } catch (e) {
            toast.update(tId, { render: apiErr(e, 'Falha ao gerar o aporte.'), type: 'error', isLoading: false, autoClose: 5000 });
        }
    };

    return (
        <div className="ap-wrap">
            <header className="ap-header">
                <div>
                    <h1>Aportes Programados{hydrated && <EnvBadge environment={environment} />}</h1>
                    <p className="ap-sub">
                        {hydrated
                            ? `Cobranças recorrentes de aporte — PIX e boleto gerados e enviados sozinhos · ${envLabelOf(environment)}`
                            : 'Confirmando ambiente da conta...'}
                    </p>
                </div>
                {hydrated && connected && tab === 'agendamentos' && (
                    <button className="ap-btn-primary" onClick={() => setEditing({})}>+ Novo aporte programado</button>
                )}
            </header>

            {hydrated && connected && !isProd && (
                <div className="ap-env-alert">
                    <span className="ap-env-alert-icon" aria-hidden="true">⚠️</span>
                    <div>
                        <strong>Você está no ambiente de TESTES (Sandbox).</strong>
                        <p>
                            Aportes programados aqui geram cobranças de teste e disparam e-mails marcados como TESTE.
                            Nenhum dinheiro real é recebido. Para valer, troque o ambiente na tela “Conta Digital Stark Bank”.
                        </p>
                    </div>
                </div>
            )}

            {!hydrated ? (
                <div className="ap-loading">Carregando...</div>
            ) : !connected ? (
                <div className="ap-notice">
                    <span className="ap-notice-icon">🔌</span>
                    <div>
                        <strong>Nenhuma conta Stark Bank conectada.</strong>
                        <p>Conecte a conta na tela “Conta Digital Stark Bank” antes de programar aportes.</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="ap-tabs-row">
                        <div className="ap-tabs">
                            <button className={`ap-tab ${tab === 'agendamentos' ? 'active' : ''}`} onClick={() => setTab('agendamentos')}>
                                Agendamentos
                            </button>
                            <button className={`ap-tab ${tab === 'historico' ? 'active' : ''}`} onClick={() => setTab('historico')}>
                                Histórico
                            </button>
                        </div>
                        {tab === 'agendamentos' && agendamentos.length > 0 && (
                            <div className="ap-total">
                                <span className="ap-total-label">Programado / mês (ativos)</span>
                                <span className="ap-total-value">{fmtBRL(totalMensal)}</span>
                            </div>
                        )}
                    </div>

                    {tab === 'agendamentos' ? (
                        <AgendamentosView
                            agendamentos={agendamentos}
                            loading={loadingAg}
                            environment={environment}
                            onNew={() => setEditing({})}
                            onEdit={setEditing}
                            onToggle={toggleAtivo}
                            onDelete={excluir}
                            onGerar={gerarAgora}
                        />
                    ) : (
                        <HistoricoView environment={environment} />
                    )}
                </>
            )}

            {editing && (
                <AgendamentoModal
                    inicial={editing}
                    environment={environment}
                    scope={scope}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

// =============================================================================
// AGENDAMENTOS — lista
// =============================================================================
function AgendamentosView({ agendamentos, loading, environment, onNew, onEdit, onToggle, onDelete, onGerar }) {
    if (loading && agendamentos.length === 0) return <div className="ap-loading">Carregando agendamentos...</div>;

    if (agendamentos.length === 0) {
        return (
            <div className="ap-empty-card">
                <h3>Nenhum aporte programado ainda</h3>
                <p>Crie um aporte recorrente: escolha o dia do mês, o valor e os e-mails. O sistema gera o PIX/boleto e envia sozinho.</p>
                <button className="ap-btn-primary" onClick={onNew}>+ Criar o primeiro</button>
            </div>
        );
    }

    return (
        <div className="ap-list">
            {agendamentos.map((ag) => (
                <article key={ag.id} className={`ap-card ${ag.ativo ? '' : 'is-paused'}`}>
                    <div className="ap-card-main">
                        <div className="ap-card-head">
                            <h3>{ag.titulo}</h3>
                            <Pill tone="method">{METODO_LABEL[ag.metodo] || ag.metodo}</Pill>
                            {!ag.ativo && <Pill tone="paused">Pausado</Pill>}
                        </div>
                        {ag.descricao && <p className="ap-card-desc">{ag.descricao}</p>}
                        <div className="ap-card-grid">
                            <div>
                                <span className="ap-k">Valor</span>
                                <span className="ap-v ap-v-money">{fmtBRL(ag.amount)}</span>
                            </div>
                            <div>
                                <span className="ap-k">Recorrência</span>
                                <span className="ap-v">Dia {ag.dia_mes} às {ag.hora}</span>
                            </div>
                            <div>
                                <span className="ap-k">Próximo disparo</span>
                                <span className="ap-v">{ag.ativo ? fmtSP(ag.proximo_disparo) : '—'}</span>
                            </div>
                            <div>
                                <span className="ap-k">Pagador</span>
                                <span className="ap-v">{ag.payer_name}</span>
                            </div>
                            <div>
                                <span className="ap-k">Destinatários</span>
                                <span className="ap-v">{asArray(ag.emails).length} e-mail{asArray(ag.emails).length !== 1 ? 's' : ''}</span>
                            </div>
                            {ag.ultima_execucao && (
                                <div>
                                    <span className="ap-k">Última execução</span>
                                    <span className="ap-v">{fmtSP(ag.ultima_execucao)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="ap-card-actions">
                        <label className="ap-switch" title={ag.ativo ? 'Pausar' : 'Ativar'}>
                            <input type="checkbox" checked={!!ag.ativo} onChange={() => onToggle(ag)} />
                            <span className="ap-switch-track"><span className="ap-switch-thumb" /></span>
                        </label>
                        <button className="ap-btn-ghost" onClick={() => onGerar(ag)}>Gerar agora</button>
                        <button className="ap-btn-ghost" onClick={() => onEdit(ag)}>Editar</button>
                        <button className="ap-btn-ghost ap-btn-danger" onClick={() => onDelete(ag)}>Excluir</button>
                    </div>
                </article>
            ))}
        </div>
    );
}

// =============================================================================
// MODAL — criar / editar agendamento
// =============================================================================
const EMPTY_FORM = {
    titulo: '', descricao: '', metodo: 'pix', amount: '',
    payer_name: '', payer_tax_id: '',
    payer_street: '', payer_complement: '', payer_district: '', payer_city: '', payer_state: '', payer_zip: '',
    dia_mes: '10', hora: '09:00', boleto_vencimento_dias: '5',
};

function AgendamentoModal({ inicial, environment, scope, onClose, onSaved }) {
    const editId = inicial?.id || null;
    const [f, setF] = useState(() => ({
        ...EMPTY_FORM,
        ...(editId ? {
            titulo: inicial.titulo || '',
            descricao: inicial.descricao || '',
            metodo: inicial.metodo || 'pix',
            amount: inicial.amount != null ? String(inicial.amount).replace('.', ',') : '',
            payer_name: inicial.payer_name || '',
            payer_tax_id: inicial.payer_tax_id || '',
            payer_street: inicial.payer_street || '',
            payer_complement: inicial.payer_complement || '',
            payer_district: inicial.payer_district || '',
            payer_city: inicial.payer_city || '',
            payer_state: inicial.payer_state || '',
            payer_zip: inicial.payer_zip || '',
            dia_mes: String(inicial.dia_mes || '10'),
            hora: inicial.hora || '09:00',
            boleto_vencimento_dias: String(inicial.boleto_vencimento_dias ?? '5'),
        } : {}),
    }));
    const [emails, setEmails] = useState(() => asArray(inicial?.emails));
    const [emailInput, setEmailInput] = useState('');
    const [saving, setSaving] = useState(false);

    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
    const isProd = environment === 'production';
    const precisaEndereco = f.metodo === 'boleto' || f.metodo === 'ambos';

    const fechar = useCallback(() => { if (!saving) onClose(); }, [saving, onClose]);
    useEscapeKey(fechar);

    const addEmail = () => {
        const e = emailInput.trim().toLowerCase();
        if (!e) return;
        if (!EMAIL_RE.test(e)) { toast.error('E-mail inválido.'); return; }
        if (emails.includes(e)) { toast.info('Esse e-mail já está na lista.'); setEmailInput(''); return; }
        if (emails.length >= MAX_EMAILS) { toast.error(`Máximo de ${MAX_EMAILS} e-mails.`); return; }
        setEmails((p) => [...p, e]);
        setEmailInput('');
    };

    const onEmailKey = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); addEmail(); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!escopoAindaVale(scope)) {
            toast.error('O ambiente da conta Stark Bank mudou. Recarregue a tela antes de salvar.', { toastId: 'ap-scope' });
            return;
        }

        if (!f.titulo.trim()) { toast.error('Informe um título.'); return; }
        const valor = parseValor(f.amount);
        if (valor === null) { toast.error(`Valor entre R$ 0,01 e R$ ${VALOR_MAX.toLocaleString('pt-BR')}.`); return; }
        if (!f.payer_name.trim()) { toast.error('Informe o nome do pagador.'); return; }
        if (!taxIdValido(f.payer_tax_id)) { toast.error('CPF/CNPJ do pagador inválido.'); return; }
        const dia = Number(f.dia_mes);
        if (!Number.isInteger(dia) || dia < 1 || dia > 31) { toast.error('Dia do mês inválido (1 a 31).'); return; }
        if (!/^([01]?\d|2[0-3]):[0-5]\d$/.test(f.hora)) { toast.error('Hora inválida (HH:MM).'); return; }
        if (emails.length === 0) { toast.error('Adicione ao menos 1 e-mail.'); return; }

        if (precisaEndereco) {
            const uf = f.payer_state.trim().toUpperCase();
            if (!f.payer_street.trim() || !f.payer_city.trim() || !uf || onlyDigits(f.payer_zip).length !== 8) {
                toast.error('Boleto exige endereço do pagador (rua, cidade, UF e CEP com 8 dígitos).');
                return;
            }
            if (!UFS.includes(uf)) { toast.error('UF inválida.'); return; }
        }

        const payload = {
            titulo: f.titulo.trim(),
            descricao: f.descricao.trim() || undefined,
            metodo: f.metodo,
            amount: valor,
            payer_name: f.payer_name.trim(),
            payer_tax_id: onlyDigits(f.payer_tax_id),
            dia_mes: dia,
            hora: f.hora,
            boleto_vencimento_dias: Number(f.boleto_vencimento_dias) || 5,
            emails,
            ...(precisaEndereco ? {
                payer_street: f.payer_street.trim(),
                payer_complement: f.payer_complement.trim(),
                payer_district: f.payer_district.trim(),
                payer_city: f.payer_city.trim(),
                payer_state: f.payer_state.trim().toUpperCase(),
                payer_zip: onlyDigits(f.payer_zip),
            } : {}),
        };

        setSaving(true);
        try {
            if (editId) await api.put(`/api/baas/account/aportes/${editId}`, payload);
            else await api.post('/api/baas/account/aportes', payload);
            if (!aliveRef.current) return;
            toast.success(editId ? 'Aporte programado atualizado.' : 'Aporte programado criado.');
            onSaved();
        } catch (e2) {
            const msg = apiErr(e2, 'Não foi possível salvar.');
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (aliveRef.current) setSaving(false);
        }
    };

    return (
        <div className="ap-modal-overlay" onClick={fechar}>
            <div className="ap-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <button className="ap-modal-close" onClick={fechar} disabled={saving} aria-label="Fechar">×</button>
                <h3>{editId ? 'Editar aporte programado' : 'Novo aporte programado'}<EnvBadge environment={environment} /></h3>

                {!isProd && (
                    <div className="ap-modal-warn">
                        <strong>Sandbox:</strong> este agendamento gera cobranças de teste e nasce preso ao ambiente sandbox.
                    </div>
                )}

                <form className="ap-form" onSubmit={submit}>
                    <label>Título
                        <input type="text" maxLength={120} value={f.titulo} onChange={set('titulo')} placeholder="Ex.: Aporte mensal — sócio" required />
                    </label>
                    <label>Descrição (opcional)
                        <input type="text" maxLength={255} value={f.descricao} onChange={set('descricao')} />
                    </label>

                    <div className="ap-field">
                        <span className="ap-field-label">Método</span>
                        <div className="ap-seg">
                            {['pix', 'boleto', 'ambos'].map((m) => (
                                <button type="button" key={m} className={`ap-seg-btn ${f.metodo === m ? 'active' : ''}`} onClick={() => setF((p) => ({ ...p, metodo: m }))}>
                                    {METODO_LABEL[m]}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="ap-form-row">
                        <label>Valor (R$)
                            <input type="text" inputMode="decimal" value={f.amount} onChange={set('amount')} placeholder="0,00" required />
                        </label>
                        <label>Dia do mês
                            <input type="number" min={1} max={31} value={f.dia_mes} onChange={set('dia_mes')} required />
                        </label>
                        <label>Hora
                            <input type="time" value={f.hora} onChange={set('hora')} required />
                        </label>
                    </div>

                    <div className="ap-form-row">
                        <label>Nome do pagador
                            <input type="text" maxLength={120} value={f.payer_name} onChange={set('payer_name')} required />
                        </label>
                        <label>CPF/CNPJ do pagador
                            <input type="text" inputMode="numeric" maxLength={18} value={f.payer_tax_id} onChange={set('payer_tax_id')} required />
                        </label>
                    </div>

                    {precisaEndereco && (
                        <>
                            <div className="ap-form-divider">Endereço do pagador (exigido no boleto)</div>
                            <div className="ap-form-row">
                                <label>CEP
                                    <input type="text" inputMode="numeric" maxLength={9} value={f.payer_zip} onChange={set('payer_zip')} placeholder="01311-200" />
                                </label>
                                <label>UF
                                    <input type="text" maxLength={2} value={f.payer_state}
                                        onChange={(e) => setF((p) => ({ ...p, payer_state: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') }))}
                                        placeholder="SP" />
                                </label>
                                <label>Venc. boleto (dias)
                                    <input type="number" min={0} max={60} value={f.boleto_vencimento_dias} onChange={set('boleto_vencimento_dias')} />
                                </label>
                            </div>
                            <label>Rua e número
                                <input type="text" maxLength={255} value={f.payer_street} onChange={set('payer_street')} />
                            </label>
                            <div className="ap-form-row">
                                <label>Bairro
                                    <input type="text" maxLength={120} value={f.payer_district} onChange={set('payer_district')} />
                                </label>
                                <label>Cidade
                                    <input type="text" maxLength={120} value={f.payer_city} onChange={set('payer_city')} />
                                </label>
                                <label>Complemento
                                    <input type="text" maxLength={255} value={f.payer_complement} onChange={set('payer_complement')} />
                                </label>
                            </div>
                        </>
                    )}

                    <div className="ap-form-divider">Enviar por e-mail para (até {MAX_EMAILS})</div>
                    <div className="ap-email-input">
                        <input
                            type="email" value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            onKeyDown={onEmailKey}
                            placeholder="nome@empresa.com e Enter"
                            disabled={emails.length >= MAX_EMAILS}
                        />
                        <button type="button" className="ap-btn-ghost" onClick={addEmail} disabled={emails.length >= MAX_EMAILS}>Adicionar</button>
                    </div>
                    {emails.length > 0 && (
                        <div className="ap-chips">
                            {emails.map((e) => (
                                <span key={e} className="ap-chip">
                                    {e}
                                    <button type="button" onClick={() => setEmails((p) => p.filter((x) => x !== e))} aria-label={`Remover ${e}`}>×</button>
                                </span>
                            ))}
                        </div>
                    )}

                    <button type="submit" className="ap-btn-primary ap-btn-block" disabled={saving}>
                        {saving ? 'Salvando...' : (editId ? 'Salvar alterações' : 'Criar aporte programado')}
                    </button>
                </form>
            </div>
        </div>
    );
}

// =============================================================================
// HISTÓRICO
// =============================================================================
function HistoricoView({ environment }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [metodo, setMetodo] = useState('');
    const [status, setStatus] = useState('');
    const [baixando, setBaixando] = useState(null);
    const LIMIT = 20;

    const aliveRef = useRef(true);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const carregar = useCallback(async (pg = 1) => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/baas/account/aportes/historico', {
                params: { limit: LIMIT, offset: pg, ...(metodo ? { metodo } : {}), ...(status ? { status } : {}) },
            });
            if (!aliveRef.current) return;
            setRows(asArray(data?.historico));
            setTotal(Number(data?.total) || 0);
            setPage(pg);
        } catch (e) {
            const msg = apiErr(e);
            if (msg && aliveRef.current) toast.error(msg);
        } finally {
            if (aliveRef.current) setLoading(false);
        }
    }, [metodo, status]);

    useEffect(() => { carregar(1); }, [carregar]);

    const baixar = async (row) => {
        if (!row.tem_pdf) { toast.info('Este aporte não tem PDF guardado.'); return; }
        setBaixando(row.id);
        try {
            const resp = await api.get(`/api/baas/account/aportes/historico/${row.id}/download`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = row.drive_file_name || `aporte_${row.competencia}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiErr(e, 'Falha ao baixar o PDF.'));
        } finally {
            if (aliveRef.current) setBaixando(null);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    return (
        <div className="ap-hist">
            <div className="ap-hist-filters">
                <select value={metodo} onChange={(e) => setMetodo(e.target.value)}>
                    <option value="">Todos os métodos</option>
                    <option value="pix">PIX</option>
                    <option value="boleto">Boleto</option>
                    <option value="ambos">PIX + Boleto</option>
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">Todos os status</option>
                    <option value="gerado">Gerado</option>
                    <option value="parcial">Parcial</option>
                    <option value="erro">Erro</option>
                </select>
                <button className="ap-btn-outline" onClick={() => carregar(page)} disabled={loading}>
                    {loading ? 'Atualizando...' : '⟳ Atualizar'}
                </button>
            </div>

            {rows.length === 0 && !loading ? (
                <div className="ap-empty">Nenhum aporte gerado ainda. Quando um agendamento disparar (ou você usar “Gerar agora”), ele aparece aqui.</div>
            ) : (
                <div className="ap-table-wrap">
                    <table className="ap-table">
                        <thead>
                            <tr>
                                <th>Gerado em</th><th>Título</th><th>Método</th>
                                <th className="ap-right">Valor</th><th>Status</th><th>E-mail</th><th>Origem</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id}>
                                    <td className="ap-nowrap">{fmtSP(r.gerado_em)}</td>
                                    <td>
                                        <div className="ap-hist-title">
                                            <strong>{r.titulo}</strong>
                                            <small>competência {r.competencia}</small>
                                        </div>
                                    </td>
                                    <td><Pill tone="method">{METODO_LABEL[r.metodo] || r.metodo}</Pill></td>
                                    <td className="ap-right ap-v-money">{fmtBRL(r.amount)}</td>
                                    <td><Pill tone={r.status === 'gerado' ? 'ok' : r.status === 'parcial' ? 'warn' : 'danger'}>{STATUS_LABEL[r.status] || r.status}</Pill></td>
                                    <td><Pill tone={r.email_status === 'sent' ? 'ok' : r.email_status === 'failed' ? 'danger' : 'neutral'}>{EMAIL_LABEL[r.email_status] || r.email_status}</Pill></td>
                                    <td>{r.origem === 'agendado' ? 'Automático' : 'Manual'}</td>
                                    <td className="ap-right">
                                        <button
                                            className="ap-btn-ghost ap-btn-sm"
                                            onClick={() => baixar(r)}
                                            disabled={!r.tem_pdf || baixando === r.id}
                                            title={r.tem_pdf ? 'Baixar PDF' : 'Sem PDF'}
                                        >
                                            {baixando === r.id ? '...' : (r.tem_pdf ? '⬇ PDF' : '—')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {total > LIMIT && (
                <div className="ap-pager">
                    <button className="ap-btn-ghost" disabled={loading || page <= 1} onClick={() => carregar(page - 1)}>← Anterior</button>
                    <span className="ap-pager-info">Página {page} de {totalPages} · {total} registro{total !== 1 ? 's' : ''}</span>
                    <button className="ap-btn-ghost" disabled={loading || page >= totalPages} onClick={() => carregar(page + 1)}>Próxima →</button>
                </div>
            )}
        </div>
    );
}